/*
 * Gift Concierge Experience
 * Every question is a section block (sortable in the theme editor). Each block can have up to
 * three "show when" rules that reference earlier questions by handle.
 *
 * On submit:
 *   1. Posts the answers as JSON to a Google Apps Script web app (writes a row to Google Sheets)
 *   2. Creates / updates a Shopify customer subscribed to email marketing (customer form)
 *   3. Sends the store contact form, one field per answer, so the admin email lists every answer
 */
(() => {
  if (window.GiftConcierge) {
    window.GiftConcierge.initAll();
    return;
  }

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const STORAGE_TTL = 1000 * 60 * 60 * 24 * 14;
  const CHOICE_TYPES = ['single', 'multi', 'dropdown'];

  const ICON_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  const ICON_ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
  const ICON_BACK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M11 18l-6-6 6-6"/></svg>';
  const ICON_LOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';

  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  /* Forgiving comparison for admin-typed rule values: case, dash/quote style and spacing don't matter */
  const norm = (s) =>
    String(s ?? '')
      .toLowerCase()
      .replace(/[‐-―−]/g, '-')
      .replace(/[‘’´`]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/\s*([\/\-&])\s*/g, '$1')
      .replace(/\s+/g, ' ')
      .trim();
  const normHandle = (s) => norm(s).replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const fill = (tpl, vars) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in vars ? vars[k] : m));
  const readStore = (store, key) => {
    try { return store.getItem(key); } catch (e) { return null; }
  };
  const writeStore = (store, key, value) => {
    try { value === null ? store.removeItem(key) : store.setItem(key, value); } catch (e) { /* storage blocked */ }
  };

  /* Options: one per line. Trailing "*" adds a free-text box, trailing "!" makes it exclusive. */
  function parseOptions(raw) {
    return String(raw || '')
      .split(/\r?\n/)
      .map((line) => {
        let label = line.trim();
        let other = false;
        let exclusive = false;
        let m;
        while ((m = label.match(/\s*([*!])$/))) {
          if (m[1] === '*') other = true;
          else exclusive = true;
          label = label.slice(0, -m[0].length);
        }
        return { label: label.trim(), other, exclusive };
      })
      .filter((o) => o.label);
  }

  /* Shopify contact fields are keyed by name, so strip characters that break the bracket syntax */
  const fieldKey = (s) => String(s || 'Answer').replace(/[\[\]\r\n]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);

  class Concierge {
    constructor(root) {
      this.root = root;
      this.cfg = JSON.parse(root.querySelector('[data-gcx-config]').textContent);
      this.id = this.cfg.sectionId;
      this.storageKey = `gcx:${this.id}:${location.pathname}`;
      this.labels = this.cfg.labels || {};
      this.messages = this.cfg.messages || {};

      this.questions = (this.cfg.questions || []).map((q, i) => ({
        ...q,
        handle: normHandle(q.handle || q.label) || `question_${i + 1}`,
        options: parseOptions(q.options),
      }));
      this.byHandle = {};
      this.questions.forEach((q) => {
        if (!this.byHandle[q.handle]) this.byHandle[q.handle] = q;
      });
      /* A rule can name the other question by handle or by its question text */
      this.questions.forEach((q) => {
        q.rules = (q.rules || [])
          .filter((r) => String(r.field || '').trim() && r.op)
          .map((r) => {
            const byText = this.questions.find((x) => x.kind === 'question' && norm(x.label) === norm(r.field));
            const field = this.byHandle[normHandle(r.field)] ? normHandle(r.field) : byText ? byText.handle : normHandle(r.field);
            return { ...r, raw: r.field, field };
          });
      });

      this.answers = {};
      this.contact = { name: '', email: '', phone: '', marketing: this.cfg.customerMode === 'checkbox_on' };
      this.current = null;
      this.editing = false;
      this.started = false;
      this.done = false;
      this.busy = false;
      this.delivered = { sheet: false, contact: false, customer: false };

      this.$ = (sel) => root.querySelector(sel);
      this.screens = {
        intro: this.$('[data-screen="intro"]'),
        flow: this.$('[data-screen="flow"]'),
        thanks: this.$('[data-screen="thanks"]'),
      };
      this.nav = {
        back: this.$('[data-gcx-back]'),
        skip: this.$('[data-gcx-skip]'),
        next: this.$('[data-gcx-next]'),
        kbd: this.$('[data-gcx-kbd]'),
      };

      this.renderSteps();
      if (this.cfg.designMode) this.renderQuestionMap();
      this.bind();

      try {
        this.track('view');
        if (this.restoreSaved()) return;
        if (/(^#|&)start\b/.test(location.hash) && !this.cfg.designMode) this.start();
      } finally {
        this.reveal();
      }
    }

    /* Undo the inline "is-resuming" guard once the correct screen is showing */
    reveal() {
      if (!this.root.classList.contains('is-resuming')) return;
      this.root.classList.remove('is-resuming');
      const shell = this.$('.gcx__shell');
      shell.classList.add('is-revealed');
      shell.addEventListener('animationend', () => shell.classList.remove('is-revealed'), { once: true });
    }

    /* ── Conditions ───────────────────────────────── */

    rawValues(q) {
      const a = this.answers[q.handle];
      if (!a) return [];
      if (CHOICE_TYPES.includes(q.fieldType)) return a.selected || [];
      return a.text ? [a.text] : [];
    }

    isVisible(q, seen = new Set()) {
      if (q.kind !== 'question' || !q.rules.length) return true;
      if (seen.has(q.handle)) return false;
      seen.add(q.handle);
      const results = q.rules.map((r) => this.testRule(r, seen));
      return q.mode === 'any' ? results.some(Boolean) : results.every(Boolean);
    }

    testRule(rule, seen) {
      const src = this.byHandle[rule.field];
      const values = src && this.isVisible(src, new Set(seen)) ? this.rawValues(src).map(norm) : [];
      const targets = String(rule.value || '').split('|').map(norm).filter(Boolean);
      const hit = (fn) => values.some((v) => targets.some((t) => fn(v, t)));
      switch (rule.op) {
        case 'is': return hit((v, t) => v === t);
        case 'is_not': return !hit((v, t) => v === t);
        case 'contains': return hit((v, t) => v.includes(t));
        case 'not_contains': return !hit((v, t) => v.includes(t));
        case 'answered': return values.length > 0;
        case 'empty': return values.length === 0;
        default: return true;
      }
    }

    visible() {
      return this.questions.filter((q) => this.isVisible(q));
    }

    /* ── Rendering ────────────────────────────────── */

    renderSteps() {
      this.questions.forEach((q) => {
        const el = this.root.querySelector(`[data-step][data-block-id="${q.id}"]`);
        if (!el) return;
        q.el = el;
        el.innerHTML = this.stepHtml(q);
        el.setAttribute('role', 'group');
        el.setAttribute('aria-labelledby', `gcx-l-${q.id}`);
      });
      this.reviewEl = this.$('[data-step-review]');
    }

    stepHtml(q) {
      const L = this.labels;
      const optional = !q.required && q.kind === 'question' ? `<span class="gcx__optional">${esc(L.optional)}</span>` : '';
      let html = this.cfg.designMode ? this.adminBadge(q) : '';
      html += `<p class="gcx__num" data-gcx-num></p>`;
      html += `<h2 class="gcx__label" id="gcx-l-${q.id}" tabindex="-1">${esc(q.label)}${optional}</h2>`;
      if (q.help) html += `<p class="gcx__help">${esc(q.help)}</p>`;
      if (q.fieldType === 'multi') {
        let hint = L.multiHint;
        if (q.max > 0) hint = fill(L.maxHint, { max: q.max });
        if (hint) html += `<p class="gcx__hint">${esc(hint)}</p>`;
      }
      html += `<div class="gcx__control">${this.controlHtml(q)}</div>`;
      html += `<p class="gcx__error" role="alert" data-gcx-error></p>`;
      return html;
    }

    controlHtml(q) {
      const L = this.labels;
      const uid = `gcx-${q.id}`;
      if (q.kind === 'contact') return this.contactHtml(q);

      if (q.fieldType === 'single' || q.fieldType === 'multi') {
        const role = q.fieldType === 'single' ? 'radio' : 'checkbox';
        const layout = q.layout && q.layout !== 'auto' ? ` gcx__options--${q.layout}` : '';
        let h = `<div class="gcx__options${layout}" role="${role === 'radio' ? 'radiogroup' : 'group'}" aria-labelledby="gcx-l-${q.id}">`;
        q.options.forEach((o, i) => {
          const [main, sub] = o.label.split(/\s+[—–]\s+/);
          h += `<button type="button" class="gcx__opt" role="${role}" aria-checked="false" data-opt="${i}">`;
          if (this.cfg.showKeys && q.options.length <= 9) h +=`<span class="gcx__opt-key" aria-hidden="true">${i + 1}</span>`;
          h += `<span class="gcx__opt-text">${esc(main)}${sub ? `<span class="gcx__opt-sub">${esc(sub)}</span>` : ''}</span>`;
          h += `<span class="gcx__opt-mark" aria-hidden="true">${ICON_CHECK}</span></button>`;
          if (o.other) {
            h += `<div class="gcx__other" data-other="${i}" hidden><input type="text" class="gcx__input" maxlength="200" placeholder="${esc(L.otherPlaceholder)}" aria-label="${esc(main)}"></div>`;
          }
        });
        return h + '</div>';
      }

      if (q.fieldType === 'dropdown') {
        let h = `<select class="gcx__select" id="${uid}" aria-labelledby="gcx-l-${q.id}"><option value="">${esc(q.placeholder || L.choose)}</option>`;
        q.options.forEach((o, i) => { h += `<option value="${i}">${esc(o.label)}</option>`; });
        h += '</select>';
        q.options.forEach((o, i) => {
          if (o.other) h += `<div class="gcx__other" data-other="${i}" hidden style="margin-top:10px"><input type="text" class="gcx__input" maxlength="200" placeholder="${esc(L.otherPlaceholder)}" aria-label="${esc(o.label)}"></div>`;
        });
        return h;
      }

      if (q.fieldType === 'long_text') {
        return `<textarea class="gcx__textarea" id="${uid}" maxlength="2000" placeholder="${esc(q.placeholder)}" aria-labelledby="gcx-l-${q.id}" data-gcx-text></textarea><p class="gcx__counter" data-gcx-count>0 / 2000</p>`;
      }
      const type = q.fieldType === 'number' ? 'number' : 'text';
      const extra = q.fieldType === 'number' ? ' inputmode="decimal" min="0"' : ' maxlength="200"';
      return `<input type="${type}" class="gcx__input" id="${uid}"${extra} placeholder="${esc(q.placeholder)}" aria-labelledby="gcx-l-${q.id}" data-gcx-text>`;
    }

    contactHtml(q) {
      const L = this.labels;
      const c = q.contact || {};
      const mode = this.cfg.customerMode;
      let h = '<div class="gcx__fields">';
      h += `<label class="gcx__field gcx__field--full"><span>${esc(c.nameLabel)}</span><input class="gcx__input" type="text" name="name" autocomplete="name" maxlength="120" placeholder="${esc(c.namePlaceholder)}" data-gcx-contact="name"><span class="gcx__field-error" data-gcx-field-error="name"></span></label>`;
      h += `<label class="gcx__field${c.showPhone ? '' : ' gcx__field--full'}"><span>${esc(c.emailLabel)}</span><input class="gcx__input" type="email" name="email" autocomplete="email" inputmode="email" maxlength="160" placeholder="${esc(c.emailPlaceholder)}" data-gcx-contact="email"><span class="gcx__field-error" data-gcx-field-error="email"></span></label>`;
      if (c.showPhone) {
        const opt = c.phoneRequired ? '' : ` <small>(${esc(L.optional)})</small>`;
        h += `<label class="gcx__field"><span>${esc(c.phoneLabel)}${opt}</span><input class="gcx__input" type="tel" name="phone" autocomplete="tel" inputmode="tel" maxlength="30" placeholder="${esc(c.phonePlaceholder)}" data-gcx-contact="phone"><span class="gcx__field-error" data-gcx-field-error="phone"></span></label>`;
      }
      if (mode === 'checkbox_on' || mode === 'checkbox_off') {
        h += `<label class="gcx__consent"><input type="checkbox" data-gcx-contact="marketing"${mode === 'checkbox_on' ? ' checked' : ''}${c.marketingRequired ? ' aria-required="true"' : ''}><span>${esc(c.marketingLabel)}</span></label>`;
        h += '<span class="gcx__field-error gcx__field-error--full" data-gcx-field-error="marketing"></span>';
      }
      h += `<input class="gcx__hp" type="text" tabindex="-1" autocomplete="off" aria-hidden="true" data-gcx-hp>`;
      if (c.privacyNote) h += `<p class="gcx__privacy">${ICON_LOCK}<span>${esc(c.privacyNote)}</span></p>`;
      return h + '</div>';
    }

    /* ── Theme editor guidance (only rendered in the customizer) ── */

    ruleText(r) {
      const ops = { is: 'is', is_not: 'is not', contains: 'contains', not_contains: "doesn't contain", answered: 'is answered', empty: 'is skipped' };
      const src = this.byHandle[r.field];
      const name = src ? `“${src.label}”` : `“${r.raw}”`;
      const values = String(r.value || '').split('|').map((v) => v.trim()).filter(Boolean).join(' or ');
      return `${name} ${ops[r.op] || r.op}${['answered', 'empty'].includes(r.op) ? '' : ` ${values || '(no answer typed)'}`}`;
    }

    ruleWarnings(q) {
      const warnings = [];
      const pos = this.questions.indexOf(q);
      q.rules.forEach((r) => {
        const src = this.byHandle[r.field];
        if (!src) {
          warnings.push(`Can't find a question called “${r.raw}”. Type an earlier question's text or its ID (shown on that question).`);
          return;
        }
        if (src === q) {
          warnings.push('A rule points at this same question. Pick an earlier question.');
          return;
        }
        if (this.questions.indexOf(src) > pos) {
          warnings.push(`“${src.label}” is below this question. Drag this question underneath it.`);
        }
        const needsValue = !['answered', 'empty'].includes(r.op);
        const values = String(r.value || '').split('|').map((v) => v.trim()).filter(Boolean);
        if (needsValue && !values.length) warnings.push('A rule is missing the answer to check for.');
        if (needsValue && CHOICE_TYPES.includes(src.fieldType) && src.options.length) {
          const opts = src.options.map((o) => norm(o.label));
          values.forEach((v) => {
            const ok = ['contains', 'not_contains'].includes(r.op) ? opts.some((o) => o.includes(norm(v))) : opts.includes(norm(v));
            if (!ok) warnings.push(`“${v}” isn't one of the answers for “${src.label}”. Check the spelling.`);
          });
        }
      });
      return warnings;
    }

    typeName(q) {
      return { single: 'Single choice', multi: 'Multiple choice', dropdown: 'Dropdown', short_text: 'Short text', long_text: 'Long text', number: 'Number', contact: 'Name, email & phone' }[q.fieldType] || q.fieldType;
    }

    adminBadge(q) {
      const num = this.questions.indexOf(q) + 1;
      let h = '<div class="gcx__admin">';
      h += `<span><b>#${num}</b> · ${esc(this.typeName(q))} · ${q.required || q.kind === 'contact' ? 'Required' : 'Optional'}`;
      if (q.kind === 'question') h += ` · ID: <b>${esc(q.handle)}</b>`;
      h += '</span>';

      if (q.kind === 'question') {
        if (q.rules.length) {
          const joiner = q.mode === 'any' ? ' <b>OR</b> ' : ' <b>AND</b> ';
          h += `<span class="gcx__admin-line">👁 Shows only when ${q.rules.map((r) => esc(this.ruleText(r))).join(joiner)}</span>`;
        } else {
          h += '<span class="gcx__admin-line">👁 Always shown</span>';
        }
        const flags = [];
        q.options.forEach((o) => {
          if (o.other) flags.push(`${esc(o.label)} → text box`);
          if (o.exclusive) flags.push(`${esc(o.label)} → clears other picks`);
        });
        if (flags.length) h += `<span class="gcx__admin-line">✎ ${flags.join(' · ')}</span>`;
      }

      const warnings = q.kind === 'question' ? this.ruleWarnings(q) : [];
      if (this.questions.filter((x) => x.handle === q.handle).length > 1) warnings.push('Another question has the same ID. Change the ID under “Advanced”.');
      if (CHOICE_TYPES.includes(q.fieldType) && q.kind === 'question' && !q.options.length) warnings.push('Add answer options, one per line.');
      if (q.kind === 'contact' && this.questions.indexOf(q) !== this.questions.length - 1) warnings.push('“Your details” should be the last block.');
      warnings.forEach((w) => { h += `<span class="gcx__admin-warn">⚠ ${esc(w)}</span>`; });
      return h + '</div>';
    }

    /* Overview of the whole questionnaire, shown on the intro screen in the customizer */
    renderQuestionMap() {
      const body = this.$('.gcx__intro-body');
      if (!body || !this.questions.length) return;
      const problems = this.questions.reduce((n, q) => n + (q.kind === 'question' ? this.ruleWarnings(q).length : 0), 0);
      let h = '<details class="gcx__map" open><summary>Question map <small>(only visible in the theme editor)</small></summary>';
      h += '<p class="gcx__map-tip">Click a question in the left sidebar to preview it. Drag blocks to reorder. Use “Add block” → Question to add one.</p>';
      if (problems) h += `<p class="gcx__admin-warn">⚠ ${problems} rule problem${problems > 1 ? 's' : ''}. Click the questions marked ⚠ for details.</p>`;
      h += '<ol>';
      this.questions.forEach((q) => {
        const warn = q.kind === 'question' && this.ruleWarnings(q).length ? ' ⚠' : '';
        const when = q.kind === 'question' && q.rules.length ? `<em>only if ${esc(q.rules.map((r) => this.ruleText(r)).join(q.mode === 'any' ? ' or ' : ' and '))}</em>` : '';
        h += `<li><span><b>${esc(q.label)}</b>${warn}</span><span class="gcx__map-meta">${esc(this.typeName(q))} · ${q.required || q.kind === 'contact' ? 'required' : 'optional'}${q.kind === 'question' ? ` · ID: ${esc(q.handle)}` : ''}</span>${when}</li>`;
      });
      body.insertAdjacentHTML('beforeend', h + '</ol></details>');
    }

    /* ── Events ───────────────────────────────────── */

    bind() {
      this.root.addEventListener('click', (e) => {
        const t = e.target.closest('button, [data-gcx-start], [data-gcx-edit], [data-gcx-restart], [data-gcx-fresh]');
        if (!t || !this.root.contains(t)) return;
        if (t.matches('[data-gcx-start]')) return this.start();
        if (t.matches('[data-gcx-fresh]')) {
          this.track('start_fresh');
          return this.reset(true);
        }
        if (t.matches('[data-gcx-restart]')) return this.reset(true);
        if (t.matches('[data-gcx-back]')) return this.back();
        if (t.matches('[data-gcx-skip]')) return this.skip();
        if (t.matches('[data-gcx-next]')) return this.next();
        if (t.matches('[data-gcx-edit]')) return this.edit(t.dataset.gcxEdit);
        if (t.matches('.gcx__opt')) return this.choose(t);
      });

      this.root.addEventListener('input', (e) => this.onInput(e));
      this.root.addEventListener('change', (e) => this.onInput(e));

      this.root.addEventListener('keydown', (e) => {
        if (this.screens.flow.hidden || this.busy) return;
        const tag = e.target.tagName;
        if (e.key === 'Enter' && tag !== 'TEXTAREA' && tag !== 'BUTTON' && tag !== 'A' && !e.isComposing) {
          e.preventDefault();
          this.next();
          return;
        }
        if (e.key === 'Enter' && tag === 'TEXTAREA' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          this.next();
          return;
        }
        if (/^[1-9]$/.test(e.key) && !['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) && !e.metaKey && !e.ctrlKey && !e.altKey) {
          const q = this.current;
          const btn = q && q.el && q.el.querySelectorAll('.gcx__opt')[Number(e.key) - 1];
          if (btn) {
            e.preventDefault();
            btn.focus();
            this.choose(btn);
          }
        }
      });

      /* A "#start" link clicked while already on this page jumps into the questionnaire */
      this.onHashChange = () => {
        if (/(^#|&)start\b/.test(location.hash) && !this.cfg.designMode && this.screens.intro && !this.screens.intro.hidden) this.start();
      };
      window.addEventListener('hashchange', this.onHashChange);

      this.onPageHide = () => {
        if (this.started && !this.done) this.track('abandon', this.stepData());
      };
      window.addEventListener('pagehide', this.onPageHide);
    }

    onInput(e) {
      const t = e.target;
      const step = t.closest('[data-step]');
      if (t.matches('[data-gcx-contact]')) {
        const key = t.dataset.gcxContact;
        this.contact[key] = key === 'marketing' ? t.checked : t.value;
        t.removeAttribute('aria-invalid');
        const fe = this.root.querySelector(`[data-gcx-field-error="${key}"]`);
        if (fe) fe.textContent = '';
        this.clearError();
        this.save();
        return;
      }
      if (!step) return;
      const q = this.questions.find((x) => x.el === step);
      if (!q) return;
      const a = this.answer(q);

      if (t.matches('select.gcx__select')) {
        a.selected = t.value === '' ? [] : [q.options[Number(t.value)].label];
        q.el.querySelectorAll('[data-other]').forEach((box) => {
          box.hidden = box.dataset.other !== t.value;
        });
        const box = q.el.querySelector(`[data-other="${t.value}"]:not([hidden]) input`);
        if (box && e.type === 'change') box.focus();
      } else if (t.closest('[data-other]')) {
        const opt = q.options[Number(t.closest('[data-other]').dataset.other)];
        a.other[opt.label] = t.value;
      } else if (t.matches('[data-gcx-text]')) {
        a.text = t.value.trim();
        const count = q.el.querySelector('[data-gcx-count]');
        if (count) count.textContent = `${t.value.length} / 2000`;
      }
      t.removeAttribute('aria-invalid');
      this.clearError();
      this.updateNav();
      this.save();
    }

    answer(q) {
      if (!this.answers[q.handle]) this.answers[q.handle] = { selected: [], other: {}, text: '' };
      return this.answers[q.handle];
    }

    choose(btn) {
      const q = this.current;
      if (!q || !q.el.contains(btn)) return;
      const opt = q.options[Number(btn.dataset.opt)];
      const a = this.answer(q);
      const isOn = a.selected.includes(opt.label);

      if (q.fieldType === 'single') {
        a.selected = [opt.label];
      } else if (isOn) {
        a.selected = a.selected.filter((l) => l !== opt.label);
      } else {
        if (opt.exclusive) a.selected = [];
        else a.selected = a.selected.filter((l) => !(q.options.find((o) => o.label === l) || {}).exclusive);
        if (q.max > 0 && a.selected.length >= q.max) {
          this.showError(fill(this.messages.max, { max: q.max }));
          return;
        }
        a.selected.push(opt.label);
      }

      this.syncStep(q);
      this.clearError();
      this.updateNav();
      this.save();

      const otherBox = q.el.querySelector(`[data-other="${btn.dataset.opt}"]:not([hidden]) input`);
      if (otherBox) {
        otherBox.focus();
      } else if (q.fieldType === 'single' && this.cfg.autoAdvance && !isOn) {
        clearTimeout(this.advanceTimer);
        this.advanceTimer = setTimeout(() => {
          if (this.current === q) this.next();
        }, Number(this.cfg.advanceDelay) || 900);
      }
    }

    syncStep(q) {
      if (!q.el) return;
      const a = this.answers[q.handle] || { selected: [], other: {}, text: '' };
      if (q.kind === 'contact') {
        q.el.querySelectorAll('[data-gcx-contact]').forEach((inp) => {
          const key = inp.dataset.gcxContact;
          if (key === 'marketing') inp.checked = !!this.contact.marketing;
          else inp.value = this.contact[key] || '';
        });
        return;
      }
      if (q.fieldType === 'single' || q.fieldType === 'multi') {
        const atMax = q.fieldType === 'multi' && q.max > 0 && a.selected.length >= q.max;
        q.el.querySelectorAll('.gcx__opt').forEach((b) => {
          const o = q.options[Number(b.dataset.opt)];
          const on = a.selected.includes(o.label);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          b.setAttribute('aria-disabled', atMax && !on ? 'true' : 'false');
        });
      }
      if (q.fieldType === 'dropdown') {
        const sel = q.el.querySelector('select');
        const idx = q.options.findIndex((o) => a.selected.includes(o.label));
        sel.value = idx === -1 ? '' : String(idx);
      }
      q.el.querySelectorAll('[data-other]').forEach((box) => {
        const o = q.options[Number(box.dataset.other)];
        box.hidden = !a.selected.includes(o.label);
        const inp = box.querySelector('input');
        if (inp.value !== (a.other[o.label] || '')) inp.value = a.other[o.label] || '';
      });
      const txt = q.el.querySelector('[data-gcx-text]');
      if (txt && txt.value.trim() !== a.text) txt.value = a.text || '';
    }

    /* ── Navigation ───────────────────────────────── */

    start() {
      if (!this.questions.length) return;
      this.started = true;
      this.show('flow');
      const first = this.visible()[0];
      this.go(first, 'forward');
      this.track('start');
    }

    resume(saved) {
      this.answers = saved.answers || {};
      Object.assign(this.contact, saved.contact || {});
      this.questions.forEach((q) => this.syncStep(q));
      this.started = true;
      this.show('flow');
      const vis = this.visible();
      const target = vis.find((q) => q.handle === saved.current) || vis.find((q) => !this.isComplete(q)) || vis[vis.length - 1];
      if (saved.current === '__review' && this.cfg.review) this.go('review', 'none');
      else this.go(target, 'none');
      this.track('resume');
    }

    reset(startNow) {
      writeStore(localStorage, this.storageKey, null);
      this.answers = {};
      this.contact = { name: '', email: '', phone: '', marketing: this.cfg.customerMode === 'checkbox_on' };
      this.delivered = { sheet: false, contact: false, customer: false };
      this.done = false;
      this.editing = false;
      this.questions.forEach((q) => this.syncStep(q));
      this.hideToast();
      if (startNow) this.start();
      else this.show('intro');
    }

    go(target, dir) {
      clearTimeout(this.advanceTimer);
      const steps = this.root.querySelectorAll('[data-step], [data-step-review]');
      steps.forEach((s) => { s.hidden = true; s.classList.remove('is-forward', 'is-back'); });

      this.current = target;
      const el = target === 'review' ? this.renderReview() : target.el;
      if (!el) return;
      el.hidden = false;
      void el.offsetWidth;
      if (dir !== 'none') el.classList.add(dir === 'back' ? 'is-back' : 'is-forward');

      if (target !== 'review') this.syncStep(target);
      if (target === 'review' || target.kind === 'contact') this.prepareCaptcha();
      this.clearError();
      this.updateProgress();
      this.updateNav();
      this.save();
      this.scrollIntoView();

      const heading = el.querySelector('.gcx__label');
      const firstInput = el.querySelector('input:not([type="checkbox"]):not([data-gcx-hp]), textarea, select');
      const touch = window.matchMedia('(hover: none)').matches;
      if (firstInput && !touch && target !== 'review' && !CHOICE_TYPES.includes(target.fieldType)) firstInput.focus({ preventScroll: true });
      else if (heading) heading.focus({ preventScroll: true });

      this.track('step', this.stepData());
    }

    nextTarget() {
      const vis = this.visible();
      const idx = vis.indexOf(this.current);
      if (this.editing) {
        const pending = vis.slice(idx + 1).find((q) => q.required && !this.isComplete(q));
        if (pending) return pending;
        return this.cfg.review ? 'review' : null;
      }
      return vis[idx + 1] || (this.cfg.review ? 'review' : null);
    }

    next() {
      if (this.busy) return;
      if (this.current === 'review') return this.submit();
      const q = this.current;
      if (!q) return;
      if (!this.validate(q)) return;
      const target = this.nextTarget();
      if (!target) return this.submit();
      if (target === 'review') this.editing = false;
      this.go(target, 'forward');
    }

    skip() {
      const q = this.current;
      if (!q || q === 'review' || q.required) return;
      delete this.answers[q.handle];
      this.syncStep(q);
      const target = this.nextTarget();
      if (!target) return this.submit();
      this.go(target, 'forward');
    }

    back() {
      if (this.busy) return;
      const vis = this.visible();
      if (this.current === 'review') return this.go(vis[vis.length - 1], 'back');
      const idx = vis.indexOf(this.current);
      if (idx > 0) return this.go(vis[idx - 1], 'back');
      this.show('intro');
    }

    edit(id) {
      const q = this.questions.find((x) => x.id === id);
      if (!q) return;
      this.editing = true;
      this.go(q, 'back');
    }

    show(name) {
      Object.entries(this.screens).forEach(([k, el]) => { if (el) el.hidden = k !== name; });
      this.scrollIntoView();
    }

    /* Height covered by sticky headers / fixed bars (theme or apps) at the top of the screen */
    topOffset() {
      if (!this.stickies) {
        this.stickies = [...document.body.querySelectorAll('*')].filter(
          (el) => !this.root.contains(el) && /sticky|fixed/.test(getComputedStyle(el).position)
        );
      }
      return this.stickies.reduce((max, el) => {
        const r = el.getBoundingClientRect();
        const isTopBar = r.height > 0 && r.height < 300 && r.top < 200 && r.bottom > 0 && r.width > window.innerWidth * 0.5;
        return isTopBar ? Math.max(max, r.bottom) : max;
      }, 0);
    }

    scrollIntoView() {
      const offset = this.topOffset();
      const top = this.root.getBoundingClientRect().top;
      if (top < offset || top > window.innerHeight * 0.6) {
        const target = Math.max(0, window.scrollY + top - offset - 12);
        const from = window.scrollY;
        window.scrollTo({ top: target, behavior: 'smooth' });
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          if (window.scrollY === from) window.scrollTo(0, target);
        }, 600);
      }
    }

    updateProgress() {
      const vis = this.visible();
      const total = vis.length;
      const isReview = this.current === 'review';
      const idx = isReview ? total : vis.indexOf(this.current);
      const pct = isReview ? 100 : Math.max(4, Math.round((idx / total) * 100));
      const fillEl = this.$('[data-gcx-progress]');
      const bar = this.$('[data-gcx-progressbar]');
      const label = this.$('[data-gcx-progress-label]');
      const pctEl = this.$('[data-gcx-progress-pct]');
      if (fillEl) fillEl.style.width = `${pct}%`;
      if (bar) bar.setAttribute('aria-valuenow', String(pct));
      if (label) label.innerHTML = isReview ? `<strong>${esc(this.labels.reviewHeading)}</strong>` : fill(esc(this.labels.progress), { current: `<strong>${idx + 1}</strong>`, total });
      if (pctEl) pctEl.textContent = fill(this.labels.percent, { percent: pct });
      if (!isReview && this.current && this.current.el) {
        const num = this.current.el.querySelector('[data-gcx-num]');
        if (num) num.textContent = this.cfg.showNumbers ? fill(this.labels.stepNumber, { current: idx + 1, total }) : '';
      }
    }

    updateNav() {
      const q = this.current;
      const L = this.labels;
      if (!q) return;
      const isReview = q === 'review';
      this.nav.skip.hidden = isReview || q.required || q.kind === 'contact';
      const target = isReview ? null : this.nextTarget();
      let label = L.next;
      let final = false;
      if (isReview || (!target && !this.cfg.review)) { label = L.submit; final = true; }
      else if (target === 'review') label = L.review;
      if (this.busy) {
        this.nav.next.innerHTML = `<span class="gcx__spinner" aria-hidden="true"></span><span>${esc(L.sending)}</span>`;
      } else {
        this.nav.next.innerHTML = `<span>${esc(label)}</span>${final ? '' : ICON_ARROW}`;
      }
      this.nav.next.disabled = this.busy;
      this.nav.back.disabled = this.busy;
      if (this.nav.kbd) this.nav.kbd.hidden = !this.cfg.showKeys || isReview || (q.fieldType === 'long_text');
    }

    /* ── Validation ───────────────────────────────── */

    isComplete(q) {
      if (q.kind === 'contact') {
        return !!(this.contact.name.trim() && EMAIL_RE.test(this.contact.email.trim()) && (!this.consentRequired(q) || this.contact.marketing));
      }
      const a = this.answers[q.handle];
      if (!a) return false;
      if (CHOICE_TYPES.includes(q.fieldType)) {
        if (!a.selected.length) return false;
        return a.selected.every((l) => {
          const o = q.options.find((x) => x.label === l);
          return !o || !o.other || (a.other[l] || '').trim();
        });
      }
      return !!a.text;
    }

    validate(q) {
      const M = this.messages;
      this.clearError();
      if (q.kind === 'contact') return this.validateContact(q);

      const a = this.answers[q.handle] || { selected: [], other: {}, text: '' };
      if (CHOICE_TYPES.includes(q.fieldType)) {
        if (!a.selected.length) {
          if (!q.required) return true;
          return this.fail(q.fieldType === 'multi' ? M.pickSome : M.pickOne);
        }
        const missingOther = a.selected.find((l) => {
          const o = q.options.find((x) => x.label === l);
          return o && o.other && !(a.other[l] || '').trim();
        });
        if (missingOther) {
          const idx = q.options.findIndex((o) => o.label === missingOther);
          const inp = q.el.querySelector(`[data-other="${idx}"] input`);
          if (inp) { inp.setAttribute('aria-invalid', 'true'); inp.focus(); }
          return this.fail(fill(M.other, { option: missingOther }));
        }
        if (q.fieldType === 'multi' && q.min > 0 && a.selected.length < q.min) return this.fail(fill(M.min, { min: q.min }));
        return true;
      }

      const inp = q.el.querySelector('[data-gcx-text]');
      if (!a.text) {
        if (!q.required) return true;
        if (inp) { inp.setAttribute('aria-invalid', 'true'); inp.focus(); }
        return this.fail(M.required);
      }
      if (q.fieldType === 'number' && Number.isNaN(Number(a.text))) {
        if (inp) inp.setAttribute('aria-invalid', 'true');
        return this.fail(M.number);
      }
      return true;
    }

    /* The opt-in checkbox only exists in the "checkbox" modes; the admin can make ticking it mandatory */
    consentRequired(q) {
      const mode = this.cfg.customerMode || '';
      return mode.startsWith('checkbox') && !!(q.contact && q.contact.marketingRequired);
    }

    validateContact(q) {
      const M = this.messages;
      const c = q.contact || {};
      const errors = {};
      const name = this.contact.name.trim();
      const email = this.contact.email.trim();
      const phone = (this.contact.phone || '').trim();
      if (!name) errors.name = M.name;
      if (!EMAIL_RE.test(email)) errors.email = M.email;
      if (c.showPhone) {
        const digits = phone.replace(/\D/g, '');
        if (!phone && c.phoneRequired) errors.phone = M.phone;
        else if (phone && (digits.length < 7 || digits.length > 15 || /[^\d\s()+.\-]/.test(phone))) errors.phone = M.phone;
      }
      if (this.consentRequired(q) && !this.contact.marketing) errors.marketing = M.consent;
      let first = null;
      ['name', 'email', 'phone', 'marketing'].forEach((k) => {
        const inp = q.el.querySelector(`[data-gcx-contact="${k}"]`);
        const fe = q.el.querySelector(`[data-gcx-field-error="${k}"]`);
        if (fe) fe.textContent = errors[k] || '';
        if (inp) {
          if (errors[k]) {
            inp.setAttribute('aria-invalid', 'true');
            first = first || inp;
          } else inp.removeAttribute('aria-invalid');
        }
      });
      if (first) {
        first.focus();
        return false;
      }
      return true;
    }

    fail(msg) {
      this.showError(msg);
      this.track('validation_error', this.stepData());
      return false;
    }

    showError(msg) {
      const el = this.current && this.current !== 'review' ? this.current.el.querySelector('[data-gcx-error]') : this.$('[data-gcx-review-error]');
      if (!el) return;
      el.textContent = '';
      void el.offsetWidth;
      el.textContent = msg;
    }

    clearError() {
      this.root.querySelectorAll('[data-gcx-error], [data-gcx-review-error]').forEach((e) => { e.textContent = ''; });
    }

    /* ── Review ───────────────────────────────────── */

    answerText(q) {
      if (q.kind === 'contact') return [this.contact.name, this.contact.email, this.contact.phone].filter(Boolean).join(' · ');
      const a = this.answers[q.handle];
      if (!a) return '';
      if (CHOICE_TYPES.includes(q.fieldType)) {
        return a.selected
          .map((l) => {
            const o = q.options.find((x) => x.label === l);
            const extra = o && o.other ? (a.other[l] || '').trim() : '';
            return extra ? `${l}: ${extra}` : l;
          })
          .join(', ');
      }
      return a.text || '';
    }

    renderReview() {
      const L = this.labels;
      const el = this.reviewEl;
      if (!el) return null;
      let h = this.cfg.designMode ? '<div class="gcx__admin"><span>Review screen (turn off under “Behaviour” to skip it)</span></div>' : '';
      h += `<h2 class="gcx__label" tabindex="-1">${esc(L.reviewHeading)}</h2>`;
      if (L.reviewText) h += `<p class="gcx__help">${esc(L.reviewText)}</p>`;
      h += '<dl class="gcx__summary">';
      this.visible().forEach((q) => {
        const text = this.answerText(q);
        h += `<div class="gcx__summary-row"><dt>${esc(q.label)}</dt><dd>${text ? esc(text) : `<em>${esc(L.skipped)}</em>`}</dd>`;
        h += `<button type="button" class="gcx__link" data-gcx-edit="${q.id}" aria-label="${esc(L.edit)}: ${esc(q.label)}">${esc(L.edit)}</button></div>`;
      });
      h += '</dl><p class="gcx__error" role="alert" data-gcx-review-error></p>';
      el.innerHTML = h;
      return el;
    }

    /* ── Submission ───────────────────────────────── */

    buildPayload() {
      const name = this.contact.name.trim();
      const parts = name.split(/\s+/);
      const marketing = this.cfg.customerMode === 'always' || ((this.cfg.customerMode || '').startsWith('checkbox') && !!this.contact.marketing);
      const answers = this.visible()
        .filter((q) => q.kind === 'question')
        .map((q) => ({ handle: q.handle, question: q.label, column: q.column || q.label, value: this.answerText(q) }));
      return {
        submitted_at: new Date().toISOString(),
        page: location.origin + location.pathname,
        name,
        first_name: parts[0] || '',
        last_name: parts.slice(1).join(' '),
        email: this.contact.email.trim(),
        phone: (this.contact.phone || '').trim(),
        marketing,
        answers,
      };
    }

    async submit() {
      if (this.busy) return;
      const vis = this.visible();
      const invalid = vis.find((q) => (q.required || q.kind === 'contact') && !this.isComplete(q));
      if (invalid) {
        this.editing = true;
        this.go(invalid, 'back');
        this.validate(invalid);
        return;
      }
      if (this.current && this.current !== 'review' && !this.validate(this.current)) return;

      const hp = this.$('[data-gcx-hp]');
      if (hp && hp.value) return this.finish();

      this.busy = true;
      this.updateNav();
      const payload = this.buildPayload();
      this.track('submit', { answers: payload.answers.length, marketing: payload.marketing });

      const sheetTask =
        this.cfg.sheetUrl && !this.delivered.sheet
          ? this.postSheet(payload).then(() => { this.delivered.sheet = true; })
          : Promise.resolve();

      /* Admin email and customer signup run one after the other, so a spam-protection
         check (if Shopify asks for one) only ever shows one popup at a time. */
      let outcome = 'ok';
      if (!this.delivered.contact) {
        try {
          outcome = await this.postContact(payload);
        } catch (err) {
          outcome = err && err.gcxCancelled ? 'cancelled' : 'error';
        }
        this.delivered.contact = outcome === 'ok';
      }
      if (outcome === 'ok' && payload.marketing && !this.delivered.customer) {
        this.delivered.customer = await this.postCustomer(payload);
      }
      await sheetTask;

      if (outcome !== 'ok') {
        this.busy = false;
        this.updateNav();
        this.showError(outcome === 'cancelled' ? this.messages.captcha : this.messages.submitError);
        this.track(outcome === 'cancelled' ? 'captcha_cancelled' : 'submit_error');
        return;
      }
      this.finish();
    }

    postSheet(payload) {
      const req = fetch(this.cfg.sheetUrl, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ ...payload, source: 'gift-concierge', shop: this.cfg.shop }),
      });
      const timeout = new Promise((resolve) => setTimeout(resolve, 8000));
      return Promise.race([req, timeout]);
    }

    /* ── Shopify spam protection (hCaptcha) ─────────
       When the store protects contact/signup forms, Shopify only accepts posts that carry an
       hCaptcha token. Shopify.captcha.protect() binds its invisible hCaptcha to our hidden forms;
       we let it run on requestSubmit(), then take the finished FormData and send it in the
       background so the customer stays on the page. */

    captchaAvailable() {
      return !!(window.Shopify && Shopify.captcha && typeof Shopify.captcha.protect === 'function');
    }

    protectForm(form) {
      if (!form || !this.captchaAvailable() || form.dataset.hcaptchaBound) return Promise.resolve();
      if (!form._gcxSubmitHooked) {
        /* Must be in place before Shopify binds hCaptcha (it keeps a reference to form.submit).
           Shopify's code then hands us the token-carrying data instead of navigating away. */
        form._gcxSubmitHooked = true;
        form.submit = () => {
          const capture = form._gcxCapture;
          if (!capture) return;
          const fd = new FormData(form);
          if (!fd.get('h-captcha-response')) return;
          form._gcxCapture = null;
          capture(fd);
        };
      }
      if (!form._gcxProtect) {
        form._gcxProtect = new Promise((resolve) => {
          try {
            Shopify.captcha.protect(form, () => resolve());
          } catch (e) {
            resolve();
          }
          setTimeout(resolve, 8000);
        });
      }
      return form._gcxProtect;
    }

    /* Load hCaptcha in the background while the customer fills in their details, so submitting feels instant */
    prepareCaptcha() {
      if (this.cfg.designMode) return;
      this.protectForm(document.getElementById(`GcxContact-${this.id}`));
      if (this.cfg.customerMode !== 'off') this.protectForm(document.getElementById(`GcxCustomer-${this.id}`));
    }

    async formDataWithCaptcha(form, fields) {
      form.querySelectorAll('[data-gcx-field]').forEach((el) => el.remove());
      fields.forEach(([name, value]) => {
        const inp = document.createElement('input');
        inp.type = 'hidden';
        inp.name = name;
        inp.value = value;
        inp.setAttribute('data-gcx-field', '');
        form.appendChild(inp);
      });

      await this.protectForm(form);
      if (!form.dataset.hcaptchaBound) return new FormData(form);

      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          form._gcxCapture = null;
          reject(new Error('captcha timeout'));
        }, 60000);
        form._gcxCapture = (fd) => {
          clearTimeout(timer);
          resolve(fd);
        };
        form.requestSubmit();
      });
    }

    async sendShopifyForm(form, fields) {
      const fd = await this.formDataWithCaptcha(form, fields);
      const res = await fetch(form.action, { method: 'POST', body: fd, credentials: 'same-origin' });
      if (/\/challenge/.test(res.url)) return this.solveChallenge(res, form.action, fd);
      return res.ok ? 'ok' : 'error';
    }

    /* Shopify flagged this visitor and replied with its "I'm not a robot" page. Instead of sending the
       customer there, take that page's security fields and captcha key, show the check in a popup on
       this page, then post the original answers + those fields, the way Shopify's own page does. */
    async solveChallenge(res, action, original) {
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const challengeForm = [...doc.querySelectorAll('form')].find((f) => f.querySelector('.h-captcha, #h-captcha'));
      const sitekey = (html.match(/sitekey\s*:\s*["']([^"']+)["']/) || [])[1];
      if (!challengeForm || !sitekey) return 'error';

      this.track('captcha_shown');
      const token = await this.askHuman(sitekey);

      const skip = ['h-captcha-response', 'g-recaptcha-response', 'form_key', 'return_path'];
      const fd = new FormData();
      for (const [name, value] of original.entries()) {
        if (!skip.includes(name)) fd.append(name, value);
      }
      challengeForm.querySelectorAll('input, textarea').forEach((el) => {
        if (!el.name || skip.includes(el.name) || el.type === 'submit' || fd.has(el.name)) return;
        fd.append(el.name, el.value);
      });
      const params = new URL(res.url).searchParams;
      if (params.get('form_key')) fd.append('form_key', params.get('form_key'));
      if (params.get('return_path')) fd.append('return_path', params.get('return_path'));
      fd.append('h-captcha-response', token);
      const retry = await fetch(action, { method: 'POST', body: fd, credentials: 'same-origin' });
      if (/\/challenge/.test(retry.url)) return 'error';
      return retry.ok ? 'ok' : 'error';
    }

    loadHcaptcha() {
      if (window.hcaptcha && typeof window.hcaptcha.render === 'function') return Promise.resolve();
      if (!this.constructor.hcaptchaLoading) {
        this.constructor.hcaptchaLoading = new Promise((resolve, reject) => {
          window.gcxHcaptchaReady = () => resolve();
          const s = document.createElement('script');
          s.src = 'https://js.hcaptcha.com/1/api.js?render=explicit&recaptchacompat=off&onload=gcxHcaptchaReady';
          s.async = true;
          s.onerror = () => reject(new Error('hcaptcha failed to load'));
          document.head.appendChild(s);
        });
      }
      return this.constructor.hcaptchaLoading;
    }

    /* Popup with a visible hCaptcha checkbox; resolves with the token once the customer passes it */
    async askHuman(sitekey) {
      await this.loadHcaptcha();
      const L = this.labels;
      let modal = this.$('[data-gcx-captcha]');
      if (!modal) {
        modal = document.createElement('div');
        modal.className = 'gcx__captcha';
        modal.setAttribute('data-gcx-captcha', '');
        modal.innerHTML =
          `<div class="gcx__captcha-card" role="dialog" aria-modal="true" aria-labelledby="gcx-captcha-title-${this.id}">` +
          `<h3 id="gcx-captcha-title-${this.id}">${esc(L.captchaHeading)}</h3>` +
          `<p>${esc(L.captchaText)}</p>` +
          '<div class="gcx__captcha-box" data-gcx-captcha-box></div>' +
          `<button type="button" class="gcx__link" data-gcx-captcha-cancel>${esc(L.captchaCancel)}</button></div>`;
        this.root.appendChild(modal);
      }
      const box = modal.querySelector('[data-gcx-captcha-box]');
      box.innerHTML = '';
      const holder = document.createElement('div');
      box.appendChild(holder);
      modal.hidden = false;

      return new Promise((resolve, reject) => {
        const close = () => {
          modal.hidden = true;
          cancel.removeEventListener('click', onCancel);
        };
        const onCancel = () => {
          close();
          const err = new Error('captcha cancelled');
          err.gcxCancelled = true;
          reject(err);
        };
        const cancel = modal.querySelector('[data-gcx-captcha-cancel]');
        cancel.addEventListener('click', onCancel);
        try {
          window.hcaptcha.render(holder, {
            sitekey,
            callback: (token) => {
              close();
              resolve(token);
            },
          });
        } catch (e) {
          close();
          reject(e);
        }
      });
    }

    async postCustomer(payload) {
      const form = document.getElementById(`GcxCustomer-${this.id}`);
      if (!form || !payload.email) return false;
      const fields = [['contact[email]', payload.email]];
      if (payload.first_name) fields.push(['contact[first_name]', payload.first_name]);
      if (payload.last_name) fields.push(['contact[last_name]', payload.last_name]);
      if (this.cfg.customerTags) fields.push(['contact[tags]', this.cfg.customerTags]);
      try {
        return (await this.sendShopifyForm(form, fields)) === 'ok';
      } catch (e) {
        return false;
      }
    }

    contactFields(payload) {
      const fields = [];
      fields.push(['contact[name]', payload.name]);
      fields.push(['contact[email]', payload.email]);
      if (payload.phone) fields.push(['contact[phone]', payload.phone]);
      const used = new Set(['name', 'email', 'phone']);
      payload.answers.forEach((a) => {
        let key = fieldKey(a.question);
        let n = 2;
        while (used.has(key.toLowerCase())) key = `${fieldKey(a.question)} (${n++})`;
        used.add(key.toLowerCase());
        fields.push([`contact[${key}]`, a.value || '—']);
      });
      return fields;
    }

    async postContact(payload) {
      const form = document.getElementById(`GcxContact-${this.id}`);
      if (!form) return 'error';
      return this.sendShopifyForm(form, this.contactFields(payload));
    }

    finish() {
      this.done = true;
      this.busy = false;
      writeStore(localStorage, this.storageKey, null);
      this.show('thanks');
      this.track('complete');
      if (this.cfg.confetti) this.confetti();
      const h = this.screens.thanks.querySelector('h2');
      if (h) h.focus({ preventScroll: true });
    }

    confetti() {
      const box = this.$('[data-gcx-confetti]');
      if (!box) return;
      const colors = [getComputedStyle(this.root).getPropertyValue('--gcx-accent'), getComputedStyle(this.root).getPropertyValue('--gcx-highlight'), '#f2c14e', '#e98a7a'];
      let html = '';
      for (let i = 0; i < 60; i++) {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.6;
        const dx = (Math.random() - 0.5) * 160;
        const r = 360 + Math.random() * 540;
        html += `<i style="left:${left}%;background:${colors[i % colors.length]};animation-delay:${delay}s;--dx:${dx}px;--r:${r}deg"></i>`;
      }
      box.innerHTML = html;
      setTimeout(() => { box.innerHTML = ''; }, 3600);
    }

    /* ── Persistence ──────────────────────────────── */

    save() {
      if (!this.cfg.saveProgress || !this.started || this.done || this.cfg.designMode) return;
      const current = this.current === 'review' ? '__review' : this.current && this.current.handle;
      const { marketing, ...contact } = this.contact;
      writeStore(localStorage, this.storageKey, JSON.stringify({ t: Date.now(), answers: this.answers, contact, current }));
    }

    loadSaved() {
      const raw = readStore(localStorage, this.storageKey);
      if (!raw) return null;
      try {
        const data = JSON.parse(raw);
        if (!data.t || Date.now() - data.t > STORAGE_TTL) {
          writeStore(localStorage, this.storageKey, null);
          return null;
        }
        return data;
      } catch (e) {
        return null;
      }
    }

    /* After a reload, drop the customer straight back on the step they were on */
    restoreSaved() {
      if (!this.cfg.saveProgress || this.cfg.designMode) return false;
      const saved = this.loadSaved();
      if (!saved || !Object.keys(saved.answers || {}).length) return false;
      this.resume(saved);
      this.showToast();
      return true;
    }

    showToast() {
      const L = this.labels;
      const toast = document.createElement('div');
      toast.className = 'gcx__toast';
      toast.setAttribute('role', 'status');
      toast.innerHTML = `<span>${esc(L.resumeText)}</span><button type="button" data-gcx-fresh>${esc(L.startOver)}</button>`;
      this.root.appendChild(toast);
      this.toast = toast;
      this.placeToast = () => {
        if (!this.toast) return;
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        const nav = this.$('.gcx__nav').getBoundingClientRect();
        const zoneTop = window.innerHeight - 16 - this.toast.offsetHeight - 8;
        const navInTheWay = nav.height > 0 && nav.bottom > zoneTop && nav.top < window.innerHeight;
        this.toast.style.bottom = mobile && navInTheWay ? `${Math.round(window.innerHeight - nav.top + 12)}px` : '';
      };
      this.placeToast();
      window.addEventListener('scroll', this.placeToast, { passive: true });
      window.addEventListener('resize', this.placeToast);
      void toast.offsetWidth; // commit the start position so the slide-in transition runs
      toast.classList.add('is-visible');
      const schedule = () => {
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => this.hideToast(), 5000);
      };
      toast.addEventListener('mouseenter', () => clearTimeout(this.toastTimer));
      toast.addEventListener('mouseleave', schedule);
      toast.addEventListener('focusin', () => clearTimeout(this.toastTimer));
      schedule();
    }

    hideToast() {
      clearTimeout(this.toastTimer);
      const toast = this.toast;
      if (!toast) return;
      this.toast = null;
      window.removeEventListener('scroll', this.placeToast);
      window.removeEventListener('resize', this.placeToast);
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 400);
    }

    /* ── Theme editor ─────────────────────────────── */

    preview(blockId) {
      const q = this.questions.find((x) => x.id === blockId);
      if (!q) return;
      this.started = true;
      this.show('flow');
      this.go(q, 'forward');
    }

    destroy() {
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('hashchange', this.onHashChange);
      clearTimeout(this.advanceTimer);
    }

    /* ── Analytics ────────────────────────────────── */

    stepData() {
      const q = this.current;
      if (!q) return {};
      if (q === 'review') return { question: 'review' };
      return { question: q.handle, step: this.visible().indexOf(q) + 1, total: this.visible().length };
    }

    track(name, data = {}) {
      if (this.cfg.designMode) return;
      const event = `gift_concierge_${name}`;
      const detail = { section: this.id, ...data };
      try {
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(event, detail);
      } catch (e) { /* analytics unavailable */ }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...detail });
      if (typeof window.gtag === 'function') window.gtag('event', event, this.analyticsParams(data));
      this.root.dispatchEvent(new CustomEvent(`gcx:${name}`, { detail, bubbles: true }));
    }

    /* Report-friendly parameters for Google Analytics (register "question" and "step_number"
       as custom dimensions in GA4 to use them in reports and funnels) */
    analyticsParams(data) {
      const params = {};
      if (data.question) params.question = data.question;
      if (data.step) params.step_number = data.step;
      if (data.total) params.total_steps = data.total;
      if (data.reason) params.reason = data.reason;
      if (data.answers !== undefined) params.answers_count = data.answers;
      const ga4 = this.ga4Ids();
      if (ga4.length) params.send_to = ga4;
      return params;
    }

    /* The store's own Google tag lists its Analytics property (G-…) next to Ads (AW-…);
       send questionnaire events to Analytics only, without hardcoding any ID */
    ga4Ids() {
      if (!this.ga4Cache) {
        const ids = new Set();
        (window.dataLayer || []).forEach((entry) => {
          if (entry && entry[0] === 'config' && /^G-/.test(String(entry[1]))) ids.add(String(entry[1]));
        });
        this.ga4Cache = [...ids];
      }
      return this.ga4Cache;
    }
  }

  const instances = new Map();

  const GiftConcierge = {
    initAll(scope = document) {
      scope.querySelectorAll('[data-gcx]').forEach((root) => {
        if (instances.has(root)) return;
        try {
          instances.set(root, new Concierge(root));
        } catch (e) {
          console.error('[Gift Concierge] failed to start', e);
        }
      });
    },
    get(root) {
      return instances.get(root);
    },
  };
  window.GiftConcierge = GiftConcierge;

  const boot = () => GiftConcierge.initAll();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => GiftConcierge.initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-gcx]').forEach((root) => {
      const inst = instances.get(root);
      if (inst) inst.destroy();
      instances.delete(root);
    });
  });
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target.closest('[data-gcx]');
    const inst = root && instances.get(root);
    if (inst) inst.preview(e.detail.blockId);
  });
})();
